import { Check as CheckIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import DictionaryAccordion from "./DictionaryAccordion";
import type { CancellationCondition } from "./cancellationConditionsApi";
import { useCancellationConditions } from "./useCancellationConditions";

interface AddConditionRowProps {
  readonly onAdd: (shortName: string, text: string, isDefault: boolean) => Promise<void>;
}

const AddConditionRow: FC<AddConditionRowProps> = ({ onAdd }) => {
  const [shortName, setShortName] = useState("");
  const [text, setText] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  return (
    <TableBody>
      <TableRow>
        <TableCell>
          <TextField
            label="Nazwa skrócona"
            fullWidth
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
          />
        </TableCell>
        <TableCell>
          <TextField
            label="Treść"
            fullWidth
            multiline
            minRows={3}
            maxRows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </TableCell>
        <TableCell>
          <FormControlLabel
            control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
            label="Domyślny"
          />
        </TableCell>
        <TableCell>
          <AsyncLoadingButton
            disabled={!shortName.trim() || !text.trim()}
            onClick={async () => {
              await onAdd(shortName.trim(), text.trim(), isDefault);
              setShortName("");
              setText("");
              setIsDefault(false);
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface EditConditionDialogProps {
  readonly open: boolean;
  readonly condition: Pick<CancellationCondition, "shortName" | "text" | "default">;
  readonly onChange: (shortName: string, text: string, isDefault: boolean) => Promise<void>;
  readonly onCancel: () => void;
}

const EditConditionDialog: FC<EditConditionDialogProps> = ({ open, condition, onChange, onCancel }) => {
  const [shortName, setShortName] = useState(() => condition.shortName);
  const [text, setText] = useState(() => condition.text);
  const [isDefault, setIsDefault] = useState(() => condition.default);

  const saveDisabled =
    !shortName.trim() ||
    !text.trim() ||
    (shortName === condition.shortName && text === condition.text && isDefault === condition.default);

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle>Modyfikuj warunek anulacji</DialogTitle>
      <DialogContent>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            marginTop: 2,
          }}
        >
          <TextField
            label="Nazwa skrócona"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Treść"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            maxRows={16}
          />
          <FormControlLabel
            control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />}
            label="Domyślny"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onCancel} />
        <AsyncLoadingButton
          disabled={saveDisabled}
          onClick={async () => {
            await onChange(shortName.trim(), text.trim(), isDefault);
          }}
        >
          Zapisz
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface ConditionRowProps {
  readonly condition: CancellationCondition;
  readonly onUpdate: (shortName: string, text: string, isDefault: boolean) => Promise<void>;
  readonly onDelete: () => Promise<void>;
}

const ConditionRow: FC<ConditionRowProps> = ({ condition, onUpdate, onDelete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  return (
    <>
      <TableRow data-testid="condition-row">
        <TableCell>{condition.shortName}</TableCell>
        <TableCell sx={{ whiteSpace: "pre-wrap" }}>{condition.text}</TableCell>
        <TableCell>{condition.default && <CheckIcon aria-label="Domyślna opcja" />}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <IconButton size="small" aria-label="Edytuj warunek anulacji" onClick={() => setDialogOpen(true)}>
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Usuń warunek anulacji ze słownika"
              onClick={() => setConfirmationOpen(true)}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
      <EditConditionDialog
        open={dialogOpen}
        condition={condition}
        onChange={async (shortName, text, isDefault) => {
          await onUpdate(shortName, text, isDefault);
          setDialogOpen(false);
        }}
        onCancel={() => setDialogOpen(false)}
      />
      <ConfirmationDialog
        open={confirmationOpen}
        title="Usuń warunek anulacji ze słownika"
        content="Czy na pewno chcesz usunąć ten warunek anulacji ze słownika?"
        onConfirm={async () => {
          await onDelete();
          setConfirmationOpen(false);
        }}
        onCancel={() => setConfirmationOpen(false)}
      />
    </>
  );
};

const CancellationConditionsDictionary = () => {
  const { conditions, isLoading, addCondition, updateCondition, deleteCondition } = useCancellationConditions();
  return (
    <DictionaryAccordion title="Warunki anulacji" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nazwa skrócona</TableCell>
            <TableCell>Treść</TableCell>
            <TableCell>Domyślny</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddConditionRow
          onAdd={async (shortName, text, isDefault) => {
            await addCondition({ shortName, text, default: isDefault });
          }}
        />
        <TableBody>
          {conditions.map((c) => (
            <ConditionRow
              key={c.id}
              condition={c}
              onUpdate={async (shortName, text, isDefault) => {
                await updateCondition(c.id, { shortName, text, default: isDefault });
              }}
              onDelete={() => deleteCondition(c.id)}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

export default CancellationConditionsDictionary;
